import { createFileRoute, useNavigate } from '@tanstack/react-router'
import React, { useEffect, useRef, useState } from 'react'
import { useEvent } from '~/client/event/use-event.hook'
import { useEventAccess } from '~/client/event/use-event-access.hook'
import { EventSpaceList, EventSpaceListMembership, ConsolidatedSpace } from '~/shared/event/event.types'
import { formatUtcDateLongFromUnknown } from '~/shared/common/date'
import { normalize, shortlistAndNormalize } from '~/client/components/present/WP2Template/transform'
import { EventSpaceStatusChoices } from '~/shared/common/enums'
import { SpaceTypeChoices, PrivacyChoices } from '~/shared/common/choices'
import { getTemplateDefinition } from '~/client/components/present/common/templateRegistry'
import {
  generatePresentation,
  buildCoverVariables,
  buildSpaceVariables,
} from '~/client/components/present/common/presentationGenerator'
import { isPresentationNotFoundError, savePresentation } from '~/client/components/present/common/presentationStorageRemote'
import { usePhotoGallery } from '~/client/file/use-photo-gallery.hook'
import { getPresentationPhotos } from '~/client/components/present/common/presentation-photos'
import { buildImageUrl } from '~/shared/common/image-url'
import type { SpaceVariables } from '~/client/components/present/common/templateDefinition'
import { NotFound } from '~/client/components/NotFound'

const MENU_CRITERIA_PLACEHOLDER = '[[Fill in details menu criteria]]'

export const Route = createFileRoute(
  '/_app/events/$eventSlug_/generate-presentation/$templateId',
)({
  component: GeneratePresentationPage,
  validateSearch: (search: { mode?: string } = {}) => ({
    mode: search.mode === 'all' ? 'all' : ('shortlisted' as const),
  }),
})

// ---------------------------------------------------------------------------
// Photo resolver — fetches gallery data for a single membership and returns
// the top 3 image URLs for the slide template.
// ---------------------------------------------------------------------------

const MembershipPhotoResolver: React.FC<{
  membership: EventSpaceListMembership
  onResolved: (membershipId: number, urls: string[]) => void
}> = ({ membership, onResolved }) => {
  const { data: galleryData } = usePhotoGallery(membership.id)
  const resolvedRef = useRef(false)

  useEffect(() => {
    if (!galleryData || resolvedRef.current) return
    resolvedRef.current = true

    const photos = getPresentationPhotos(
      galleryData.all_photos,
      membership.id,
      true,
    )

    const urls = [0, 1, 2].map((i) => {
      const photo = photos[i]
      if (!photo) return ''
      return (
        photo.url ||
        (photo.upload
          ? buildImageUrl(
              photo.upload.startsWith('media/')
                ? photo.upload
                : `media/${photo.upload}`,
              { w: 1200, h: 1000 },
            )
          : '')
      )
    })

    onResolved(membership.id, urls)
  }, [galleryData, membership.id, onResolved])

  return null
}

// ---------------------------------------------------------------------------
// Main generation page
// ---------------------------------------------------------------------------

function GeneratePresentationPage() {
  const navigate = useNavigate()
  const { eventSlug, templateId } = Route.useParams()
  const { mode } = Route.useSearch()
  const eventAccessQuery = useEventAccess(eventSlug)
  const { data: event, isLoading } = useEvent(eventSlug)

  const [phase, setPhase] = useState<
    'loading' | 'resolving-photos' | 'generating' | 'done' | 'error'
  >('loading')
  const [errorMessage, setErrorMessage] = useState('')
  const [notFound, setNotFound] = useState(false)
  const [resolvedPhotos, setResolvedPhotos] = useState<
    Map<number, string[]>
  >(new Map())

  const generatedRef = useRef(false)

  // Derived data
  const eventSpaceList: EventSpaceList | null = event
    ? (event as EventSpaceList)
    : null

  const rawMemberships: EventSpaceListMembership[] = (event?.space_memberships ||
    []) as EventSpaceListMembership[]

  const eventSpaceMemberships: EventSpaceListMembership[] =
    mode === 'all'
      ? normalize(rawMemberships)
      : shortlistAndNormalize(rawMemberships)

  // Validate template exists
  const templateDef = getTemplateDefinition(templateId)

  // Inject the template's Google Fonts so the reflow pass measures text with
  // the right font metrics instead of a system fallback. Mirrors the font
  // loading done by the presentation viewer.
  useEffect(() => {
    if (!templateDef?.googleFontsUrl) return

    const headLinks: HTMLLinkElement[] = []

    const preconnect1 = document.createElement('link')
    preconnect1.rel = 'preconnect'
    preconnect1.href = 'https://fonts.googleapis.com'
    document.head.appendChild(preconnect1)
    headLinks.push(preconnect1)

    const preconnect2 = document.createElement('link')
    preconnect2.rel = 'preconnect'
    preconnect2.href = 'https://fonts.gstatic.com'
    preconnect2.crossOrigin = ''
    document.head.appendChild(preconnect2)
    headLinks.push(preconnect2)

    const fontLink = document.createElement('link')
    fontLink.rel = 'stylesheet'
    fontLink.href = templateDef.googleFontsUrl
    document.head.appendChild(fontLink)
    headLinks.push(fontLink)

    return () => {
      headLinks.forEach((el) => el.remove())
    }
  }, [templateDef?.googleFontsUrl])

  // Transition from loading → resolving-photos once event data arrives
  useEffect(() => {
    if (isLoading) return

    if (!eventSpaceList) {
      if (eventAccessQuery.isLoading) return
      setPhase('error')
      setErrorMessage(`Event "${eventSlug}" could not be loaded.`)
      return
    }

    if (!templateDef) {
      setNotFound(true)
      return
    }

    if (eventSpaceMemberships.length === 0) {
      // No memberships — skip photo resolution, go straight to generating
      setPhase('generating')
      return
    }

    setPhase('resolving-photos')
  }, [isLoading, eventAccessQuery.isLoading, eventSpaceList, templateDef, eventSpaceMemberships.length, eventSlug, templateId])

  // Handle photo resolution callback
  const handlePhotoResolved = React.useCallback(
    (membershipId: number, urls: string[]) => {
      setResolvedPhotos((prev) => {
        const next = new Map(prev)
        next.set(membershipId, urls)
        return next
      })
    },
    [],
  )

  // Once all photos are resolved, move to generating phase
  useEffect(() => {
    if (phase !== 'resolving-photos') return
    if (resolvedPhotos.size >= eventSpaceMemberships.length) {
      setPhase('generating')
    }
  }, [phase, resolvedPhotos.size, eventSpaceMemberships.length])

  // Also handle a timeout for photo resolution (don't wait forever)
  useEffect(() => {
    if (phase !== 'resolving-photos') return

    const timeout = setTimeout(() => {
      // Proceed even if not all photos resolved
      setPhase('generating')
    }, 10000) // 10 second timeout

    return () => clearTimeout(timeout)
  }, [phase])

  // Generate the presentation once we're in the generating phase
  useEffect(() => {
    if (phase !== 'generating' || generatedRef.current) return
    if (!eventSpaceList || !templateDef) return

    generatedRef.current = true

    async function generate() {
      try {
        // Wait for template fonts to load so the reflow pass measures text
        // with the correct font metrics (Lora/Inter vs. system fallback).
        // Cap the wait so a stalled font fetch can't block generation.
        if (templateDef!.reflow && typeof document !== 'undefined' && document.fonts) {
          await Promise.race([
            document.fonts.ready,
            new Promise((resolve) => setTimeout(resolve, 3000)),
          ])
        }

        // Build cover variables
        const eventDate =
          formatUtcDateLongFromUnknown(
            eventSpaceList!.event_space_list_dates?.[0]?.start_date,
            'en-US',
          ) ?? ''

        const coverVars = buildCoverVariables({
          name: eventSpaceList!.name,
          eventDate,
          venueGroupName: eventSpaceList!.venue_group_name ?? '',
        })

        // Build space variables for each membership
        const spaceVarsArray: SpaceVariables[] = eventSpaceMemberships.map(
          (membership, index) => {
            const consolidatedSpace =
              membership.consolidatedSpace as ConsolidatedSpace
            const imageUrls = resolvedPhotos.get(membership.id) || [
              '',
              '',
              '',
            ]

            return buildSpaceVariables(
              membership,
              consolidatedSpace || {},
              index,
              imageUrls,
            )
          },
        )

        // Criteria slide content (Katten and any future template with a
        // criteria slot). Missing event fields produce empty strings, which
        // the template collapses via `conditionalVariable`.
        //   - Location → event.location followed (on a second line) by the
        //     space-type / privacy-requirement qualifier (e.g. "Midtown
        //     Manhattan\nPrivate Dining Room or Buyout"). Both halves are
        //     looked up in the choice maps so we display labels, not keys.
        //   - Format   → event.event_type (the user-facing "Type of Event"),
        //     not event.event_format which is an unrelated lookup.
        //   - Menu     → event.food_package + event.beverage_service joined
        //     with a newline, or a client-fill placeholder when both are
        //     empty.
        const attendees = eventSpaceList!.num_attendees
        const lookupLabel = (
          key: string | null | undefined,
          choices: Record<string, string>,
        ): string => {
          if (!key) return ''
          const k = key.trim()
          if (!k) return ''
          return choices[k] ?? k
        }
        // `space_type` is a multi-select (string[]) in the schema; map each
        // key to its label and join with " or ".
        const spaceTypeLabel = (eventSpaceList!.space_type ?? [])
          .map((k) => lookupLabel(k, SpaceTypeChoices))
          .filter(Boolean)
          .join(' or ')
        const privacyLabel = lookupLabel(
          eventSpaceList!.privacy_requirement,
          PrivacyChoices,
        )
        const locationParts = [
          eventSpaceList!.location?.trim() ?? '',
          [spaceTypeLabel, privacyLabel].filter(Boolean).join(' or '),
        ].filter((v) => v !== '')
        const menuParts = [
          eventSpaceList!.food_package,
          eventSpaceList!.beverage_service,
        ]
          .map((v) => (v ?? '').trim())
          .filter((v) => v !== '')
        // Time — display_json carries the optional start_time / end_time pair
        // entered on the event form. Either half may be missing; join with " - "
        // only when both are present.
        const dj = eventSpaceList!.display_json
        const startTime =
          dj && typeof dj === 'object' && 'start_time' in dj && dj.start_time
            ? String(dj.start_time).trim()
            : ''
        const endTime =
          dj && typeof dj === 'object' && 'end_time' in dj && dj.end_time
            ? String(dj.end_time).trim()
            : ''
        const eventTime = [startTime, endTime].filter(Boolean).join(' - ')

        const criteriaVars: Record<string, string> = {
          eventLocation: locationParts.join('\n'),
          eventTime,
          eventFormat: eventSpaceList!.event_type ?? '',
          eventMenu:
            menuParts.length > 0
              ? menuParts.join('\n')
              : MENU_CRITERIA_PLACEHOLDER,
          eventAttendees:
            attendees != null ? `${attendees} guests` : '',
        }

        // Venues Considered slide — alphabetized list of every venue in the
        // event, including default `new` memberships. Split into two
        // roughly-balanced columns of newline-separated "• Name" entries to
        // override the generator's default order-preserving split.
        const venuesConsideredNames = normalize(rawMemberships)
          .map((m) => {
            const cs = m.consolidatedSpace as ConsolidatedSpace | undefined
            return cs?.venue?.name ?? ''
          })
          .filter((name) => name.trim() !== '')
          .sort((a, b) =>
            a.localeCompare(b, 'en', { sensitivity: 'base' }),
          )
        const vcHalf = Math.ceil(venuesConsideredNames.length / 2)
        const formatBullets = (list: string[]) =>
          list.map((name) => `• ${name}`).join('\n')
        const venuesConsideredVars = {
          venuesList: formatBullets(venuesConsideredNames),
          venuesColumn1: formatBullets(venuesConsideredNames.slice(0, vcHalf)),
          venuesColumn2: formatBullets(venuesConsideredNames.slice(vcHalf)),
        }

        // `shortlistedVenues` is the bullet-formatted list of every venue
        // in the event whose status is Contacted, Details Pending, Details
        // Received, Booked, or Passed (i.e. anything beyond the default
        // "New" state). Available to every non-cover slide.
        const SHORTLIST_STATUSES = new Set<EventSpaceStatusChoices>([
          EventSpaceStatusChoices.CONTACTED,
          EventSpaceStatusChoices.DETAILS_PENDING,
          EventSpaceStatusChoices.DETAILS_RECEIVED,
          EventSpaceStatusChoices.BOOKED,
          EventSpaceStatusChoices.PASS,
        ])
        const shortlistedVenues = normalize(rawMemberships)
          .filter((m) => SHORTLIST_STATUSES.has(m.status))
          .map((m) => {
            const cs = m.consolidatedSpace as ConsolidatedSpace | undefined
            return cs?.venue?.name ?? ''
          })
          .filter((name) => name.trim() !== '')
          .map((name) => `• ${name}`)
          .join('\n')

        // Summary Comparison slide — always restrict to favorited
        // (is_shortlisted) venues regardless of the route's `mode`. The
        // generator paginates and builds each per-page cell from this array,
        // and `buildSummaryComparisonVars` already prefers locationNotes for
        // Location and concatenates foodPackage + beveragePackage for Menu.
        const summaryComparisonSpaceVars = spaceVarsArray.filter(
          (_, idx) => !!eventSpaceMemberships[idx]?.is_shortlisted,
        )

        // Generate the presentation
        const presentation = generatePresentation(
          templateDef!,
          eventSlug,
          coverVars,
          spaceVarsArray,
          {
            criteriaVars,
            venuesConsideredVars,
            summaryComparisonSpaceVars,
            sharedVars: { shortlistedVenues },
          },
        )

        // Save to the backend before opening Slide Studio.
        await savePresentation(presentation)

        setPhase('done')

        // Navigate to the presentation editor
        navigate({
          to: '/presentation-editor/$presentationId',
          params: { presentationId: presentation.id },
          replace: true,
        })
      } catch (err) {
        console.error('Failed to generate presentation:', err)
        if (isPresentationNotFoundError(err)) {
          setNotFound(true)
          return
        }
        setPhase('error')
        setErrorMessage(
          err instanceof Error
            ? err.message
            : 'An unexpected error occurred while generating the presentation.',
        )
      }
    }

    generate()
  }, [
    phase,
    eventSpaceList,
    templateDef,
    eventSpaceMemberships,
    resolvedPhotos,
    eventSlug,
    navigate,
  ])

  // Render photo resolvers (invisible components that fetch gallery data)
  const photoResolvers =
    phase === 'resolving-photos'
      ? eventSpaceMemberships.map((membership) => (
          <MembershipPhotoResolver
            key={membership.id}
            membership={membership}
            onResolved={handlePhotoResolved}
          />
        ))
      : null

  // Status messages for each phase
  const statusMessages: Record<string, { title: string; subtitle: string }> = {
    loading: {
      title: 'Loading event data…',
      subtitle: 'Fetching venue and space information.',
    },
    'resolving-photos': {
      title: 'Loading photos…',
      subtitle: `Resolved ${resolvedPhotos.size} of ${eventSpaceMemberships.length} venue photos.`,
    },
    generating: {
      title: 'Generating presentation…',
      subtitle: 'Building slides from template.',
    },
    done: {
      title: 'Opening editor…',
      subtitle: 'Your presentation is ready.',
    },
  }

  if (
    notFound ||
    eventAccessQuery.data?.access === 'forbidden' ||
    eventAccessQuery.data?.access === 'not_found'
  ) {
    return <NotFound />
  }

  // Error state
  if (phase === 'error') {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-[#fafafa]">
        <div className="flex flex-col items-center gap-4 max-w-md text-center">
          <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-red-500"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-800">
            Generation Failed
          </h2>
          <p className="text-sm text-gray-600">{errorMessage}</p>
          <button
            onClick={() =>
              navigate({
                to: '/events/$eventSlug',
                params: { eventSlug },
                search: { tab: 'shortlist' },
              } as any)
            }
            className="mt-2 px-5 py-2.5 rounded-lg bg-[#8A3DFF] hover:bg-[#7835e6] text-white text-sm font-medium transition-colors"
          >
            Back to Event
          </button>
        </div>
      </div>
    )
  }

  // Loading / progress state
  const status = statusMessages[phase] || statusMessages.loading

  return (
    <div className="w-full h-screen flex items-center justify-center bg-[#fafafa]">
      {/* Hidden photo resolvers */}
      {photoResolvers}

      <div className="flex flex-col items-center gap-5">
        {/* Spinner */}
        <div className="relative w-12 h-12">
          <div className="absolute inset-0 rounded-full border-2 border-gray-200" />
          <div className="absolute inset-0 rounded-full border-2 border-t-[#8A3DFF] animate-spin" />
        </div>

        <div className="text-center">
          <h2 className="text-base font-semibold text-gray-800 mb-1">
            {status.title}
          </h2>
          <p className="text-sm text-gray-500">{status.subtitle}</p>
        </div>

        {/* Progress dots */}
        <div className="flex items-center gap-2 mt-2">
          {['loading', 'resolving-photos', 'generating', 'done'].map(
            (step, i) => {
              const phases = [
                'loading',
                'resolving-photos',
                'generating',
                'done',
              ]
              const currentIndex = phases.indexOf(phase)
              const isComplete = i < currentIndex
              const isCurrent = i === currentIndex

              return (
                <div
                  key={step}
                  className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                    isComplete
                      ? 'bg-[#8A3DFF]'
                      : isCurrent
                        ? 'bg-[#8A3DFF] animate-pulse scale-125'
                        : 'bg-gray-200'
                  }`}
                />
              )
            },
          )}
        </div>
      </div>
    </div>
  )
}
